import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";

import OrganizationFunctionsService from "src/modules/data-protection/apis/OrganizationFunctionsService";
import {
    ORGANIZATION_FUNCTIONS_LIST_QUERY_KEY,
    RESERVED_FUNCTION_KEYS,
} from "src/modules/data-protection/constants/organizationFunctions";

const AI_GOVERNANCE_USER_KEYS = [
    "defaultAiGovernanceUser",
    "default_ai_governance_user",
];

const IT_SECURITY_USER_KEYS = [
    "defaultItSecurityUser",
    "default_it_security_user",
];

const extractFunctions = (payload) => {
    if (Array.isArray(payload?.functions)) {
        return payload.functions;
    }

    if (Array.isArray(payload?.data?.functions)) {
        return payload.data.functions;
    }

    if (Array.isArray(payload?.data)) {
        return payload.data;
    }

    return [];
};

const getUserDisplayName = (user) => {
    if (!user || typeof user !== "object") {
        return "";
    }

    if (user.full_name) {
        return user.full_name;
    }

    return [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
};

export const toRoutingUserOption = (user) => {
    if (!user || typeof user !== "object" || user.id == null) {
        return null;
    }

    return {
        value: user.id,
        label: getUserDisplayName(user) || String(user.id),
    };
};

const pickUserFromSource = (source, keys) => {
    if (!source || typeof source !== "object") {
        return null;
    }

    for (const key of keys) {
        const value = source[key];

        if (value && typeof value === "object" && !Array.isArray(value)) {
            return value;
        }
    }

    return null;
};

export const getFunctionRoutingUser = (payload, keys) => {
    if (!payload || typeof payload !== "object") {
        return null;
    }

    const fromDefaults = pickUserFromSource(payload.creation_defaults, keys);

    if (fromDefaults) {
        return fromDefaults;
    }

    return pickUserFromSource(payload, keys);
};

export const getAiGovernanceRoutingUser = (payload) =>
    getFunctionRoutingUser(payload, AI_GOVERNANCE_USER_KEYS);

export const getItSecurityRoutingUser = (payload) =>
    getFunctionRoutingUser(payload, IT_SECURITY_USER_KEYS);

export const mapAssignedUsersToOptions = (users) => {
    if (!Array.isArray(users)) {
        return [];
    }

    return users.map(toRoutingUserOption).filter(Boolean);
};

const findFunctionByKey = (functions, key) =>
    (functions || []).find((item) => item?.key === key) || null;

const useFunctionRoutingDefaults = (t, { notifyError = false } = {}) => {
    const functionsQuery = useQuery({
        queryKey: ORGANIZATION_FUNCTIONS_LIST_QUERY_KEY,
        queryFn: async () => {
            const service = OrganizationFunctionsService.getInstance();
            const data = await service.list();

            return extractFunctions(data);
        },
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === "development") {
                console.error(error);
            }

            if (!notifyError) {
                return;
            }

            toast(t("An error occurred while fetching functions."), {
                type: "error",
            });
        },
    });

    const functions = functionsQuery.data;

    const aiGovernanceFunction = useMemo(
        () => findFunctionByKey(functions, RESERVED_FUNCTION_KEYS.AI_GOVERNANCE),
        [functions]
    );

    const itSecurityFunction = useMemo(
        () => findFunctionByKey(functions, RESERVED_FUNCTION_KEYS.IT_SECURITY),
        [functions]
    );

    const aiGovernanceOptions = useMemo(
        () => mapAssignedUsersToOptions(aiGovernanceFunction?.assigned_users),
        [aiGovernanceFunction]
    );

    const itSecurityOptions = useMemo(
        () => mapAssignedUsersToOptions(itSecurityFunction?.assigned_users),
        [itSecurityFunction]
    );

    return {
        isFunctionsLoading: functionsQuery.isFetching,
        aiGovernanceTitle: aiGovernanceFunction?.title || t("AI Governance"),
        itSecurityTitle: itSecurityFunction?.title || t("IT Security"),
        aiGovernanceOptions,
        itSecurityOptions,
    };
};

export default useFunctionRoutingDefaults;
